import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFetchBaseQuery } from "../apiFetchBaseQuery";
import {
	AgencyProfileUpdateHandles,
	GetAgencyAdminUserListParams,
	GetAgencyListRequestParams,
	GetAgencyUsersListParams,
	GetManageAgencyBillingCardsResponse,
	GetManageAgencyInvoicesResponse,
	GetManageAgencyMappedUsersListResponse,
	GetManageAgencyNotesListResponse,
	GetManageAgencyPaymentsResponse,
	GetManageAgencyProductByIdResponse,
	GetManageAgencyProductsListResponse,
	GetManageAgencyProfileMetaResponse,
	GetManageAgencyUnmappedUsersListResponse,
	GetManageAgencyUserResponse,
	GetManageAgencyUserSetupResponse,
	GetManageAgencyUsersListResponse,
	GetManagedAgencyBillingProfileMetaResponse,
	GetManagedAgencyListResponse,
	GetManagedAgencyWhitelabelResponse,
	GetManageUserListResponse,
	GetUserEmailTemplatesResponse,
	GetUserMappedAgenciesListResponse,
	ManageAgencyAddNewCardResponse,
	ManageAgencyBillingAddCardPayload,
	ManageAgencyBillingChargeCardPayload,
	ManageAgencyChargeCardResponse,
	ManageAgencyCreatePayload,
	ManageAgencyCreateResponse,
	ManageAgencyInvoicesPaymentDetails,
	ManageAgencyInvoicesPaymentPayload,
	ManageAgencyProfileMetaData,
	ManageAgencyUserSetupPayload,
	ManageAgencyWhitelabelConfigUpdatePayload,
	ManageUserSetupPayload,
	GetManageAgencyWebauthnConfigResponse,
	UpdateManageAgencyWebauthnConfigPayload,
	UpdateManageAgencyWebauthnConfigResponse,
} from "@/types/AccountManagement";
import { DefaultApiSuccessResponse } from "@/types/ApiResponse";

const initialState: ManageAgencyProfileMetaData = {
	contact: {
		company_name: "",
		first_name: "",
		last_name: "",
		email: "",
		phone: "",
		address: "",
		city: "",
		state: "",
		postal: "",
	},
	quickbooks: {
		is_order_tracker: "N",
		order_show_billing: "N",
		whitelabel_parent_agency_id: "0",
		quickbooks_skip: "N",
		quickbooks_id: "",
		payment_terms: "Due on receipt",
	},
	notifications: {
		quickbooks_billing_contact: "",
		quickbooks_billing_email: "",
		quickbooks_billing_phone: "",
		notification_email: "",
		notification_phone: "",
		renewal_reminder: "N",
		is_report_email_notify: "N",
		payment_received_notify: "N",
	},
	campaign_goals: {
		campaign_end_default_days: "30",
		target_display_ctr: "0.0000",
		quickbooks_billing_email: "",
		eblast_interactions_low: "0.0000",
		eblast_interactions_high: "0.0000",
		eblast_clicks_low: "0.0000",
		eblast_clicks_high: "0.0000",
	},
	promotion: {
		promotion_is_active: "N",
		promotion_amount: "0.00",
		promotion_end_date: "",
		promotion_last_send_date: "0000-00-00",
		is_sent_today: false,
	},
	campaign_settings: {
		reporting_default_template: "default",
		is_campaign_autorenew: "N",
		is_csv_only_reporting: "N",
		is_geo_fence_locations: "N",
		is_automated_cc_billing: "N",
		prodata_platform_agency_id: "",
		retargeting_js: "",
	},
	webauthn_config: {
		is_2fa_enabled: "N",
		is_passkey_enabled: "N",
	},
};

export const DefaultPaymentDetails: ManageAgencyInvoicesPaymentDetails = {
	pay_type: "",
	transaction_id: "",
	invoice_amount: 0,
	transaction_fee: 0,
	amount_to_apply: 0,
	invoices_to_apply: [],
	is_cc_charge: false,
	remaining_applicable_amount: 0,
};

export const accountManagementApiSlice = createApi({
	reducerPath: "accountManagementApiSlice",
	baseQuery: apiFetchBaseQuery,
	tagTypes: [
		"getAgencyList",
		"getAgencyWhitelabel",
		"getBillingProfileMeta",
		"getAgencyBillingMeta",
		"getAgencyUnpaidInvoices",
		"getAgencyInvoicesPayments",
		"getAgencyCardsList",
		"getAgencyProfileMeta",
		"getAgencyProducts",
		"getAgencyNotes",
		"getAgencyMappedUsers",
		"getAgencyUnmappedUsers",
		"getAgencyUsersList",
		"getManageUserList",
		"getUserMappedAgencies",
	],
	endpoints: builder => ({
		getManageAgencyProfileMeta: builder.query<GetManageAgencyProfileMetaResponse, { agencyId: number }>({
			query: ({ agencyId }) => ({
				url: `manage/agency/${agencyId}/profile/meta`,
			}),
			providesTags: ["getAgencyProfileMeta"],
			keepUnusedDataFor: 1,
		}),

		getManagedAgencyList: builder.query<GetManagedAgencyListResponse, GetAgencyListRequestParams>({
			query: ({ pageNo, pageSize, filter, sorting, search = "" }) => ({
				url: `manage/agency/list/${filter.state}/${sorting.id}/${sorting.dir}`,
				params: {
					page_no: pageNo,
					limit: pageSize,
					search,
				},
			}),
			providesTags: ["getAgencyList"],
		}),

		getManageAgencyWhitelabel: builder.query<GetManagedAgencyWhitelabelResponse, { agencyId: number }>({
			query: ({ agencyId }) => ({
				url: `manage/agency/${agencyId}/whitelabel`,
			}),
			providesTags: ["getAgencyWhitelabel"],
		}),

		updateManageAgencyWhitelabel: builder.mutation<
			GetManagedAgencyWhitelabelResponse,
			{ agencyId: number; data: ManageAgencyWhitelabelConfigUpdatePayload }
		>({
			query: ({ agencyId, data }) => ({
				url: `manage/agency/${agencyId}/whitelabel`,
				method: "PUT",
				body: data,
			}),
		}),

		getManageAgencyBillingMeta: builder.query<GetManagedAgencyBillingProfileMetaResponse, { agencyId: number }>({
			query: ({ agencyId }) => ({
				url: `manage/agency/${agencyId}/billing/profile/meta`,
			}),
			providesTags: ["getAgencyBillingMeta"],
		}),

		getManageAgencyBillingUnpaidInvoices: builder.query<
			GetManageAgencyInvoicesResponse,
			{ agencyId: number; childAgencyIds: string[] }
		>({
			query: ({ agencyId, childAgencyIds = [] }) => ({
				url: `manage/agency/${agencyId}/billing/profile/invoices`,
				params: {
					child_agency_ids: childAgencyIds.join(","),
				},
			}),
			providesTags: ["getAgencyUnpaidInvoices"],
		}),

		getManageAgencyBillingPayments: builder.query<GetManageAgencyPaymentsResponse, { agencyId: number }>({
			query: ({ agencyId }) => ({
				url: `manage/agency/${agencyId}/billing/profile/payments`,
			}),
			providesTags: ["getAgencyInvoicesPayments"],
		}),

		applyManageAgencyInvoicesPayment: builder.mutation<
			ManageAgencyAddNewCardResponse,
			{ agencyId: number; data: ManageAgencyInvoicesPaymentPayload }
		>({
			query: ({ agencyId, data }) => ({
				url: `manage/agency/${agencyId}/billing/payment/invoices`,
				method: "POST",
				body: data,
			}),
			// Removed invalidatesTags since we handle refetch manually in the component
		}),

		writeOffManageAgencyInvoice: builder.mutation<DefaultApiSuccessResponse, { agencyId: number; invoiceId: number }>({
			query: ({ agencyId, invoiceId }) => ({
				url: `manage/agency/${agencyId}/billing/invoices/${invoiceId}/write-off`,
				method: "POST",
			}),
		}),

		adjustManageAgencyInvoiceAmount: builder.mutation<
			DefaultApiSuccessResponse,
			{ agencyId: number; invoiceId: number; amount: number }
		>({
			query: ({ agencyId, invoiceId, amount }) => ({
				url: `manage/agency/${agencyId}/billing/invoices/${invoiceId}/amount`,
				method: "PUT",
				body: { amount },
			}),
		}),

		getManageAgencyBillingCardsList: builder.query<GetManageAgencyBillingCardsResponse, { agencyId: number }>({
			query: ({ agencyId }) => ({
				url: `manage/agency/${agencyId}/billing/card/list`,
			}),
			providesTags: ["getAgencyCardsList"],
		}),

		createNewManageAgency: builder.mutation<ManageAgencyCreateResponse, { data: ManageAgencyCreatePayload }>({
			query: ({ data }) => ({
				url: "manage/agency",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["getAgencyList"],
		}),

		deleteManageAgency: builder.mutation<DefaultApiSuccessResponse, { agencyId: number }>({
			query: ({ agencyId }) => ({
				url: `manage/agency/${agencyId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["getAgencyList"],
		}),

		getManageAgencyProducts: builder.query<GetManageAgencyProductsListResponse, { agencyId: number }>({
			query: ({ agencyId }) => ({
				url: `manage/agency/${agencyId}/profile/products`,
			}),
			providesTags: ["getAgencyProducts"],
		}),

		getManageAgencyProductById: builder.query<
			GetManageAgencyProductByIdResponse,
			{ agencyId: number; productId: number }
		>({
			query: ({ agencyId, productId }) => ({
				url: `manage/agency/${agencyId}/profile/products/${productId}`,
			}),
		}),

		deleteManageAgencyProductById: builder.mutation<unknown, { agencyId: number; productId: number }>({
			query: ({ agencyId, productId }) => ({
				url: `manage/agency/${agencyId}/profile/products/${productId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["getAgencyProducts"],
		}),

		addManageAgencyNewCard: builder.mutation<
			ManageAgencyAddNewCardResponse,
			{ agencyId: number; data: ManageAgencyBillingAddCardPayload }
		>({
			query: ({ agencyId, data }) => ({
				url: `manage/agency/${agencyId}/billing/card/add`,
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["getAgencyCardsList"],
		}),

		getManageAgencyNotes: builder.query<GetManageAgencyNotesListResponse, { agencyId: number }>({
			query: ({ agencyId }) => ({
				url: `manage/agency/${agencyId}/profile/notes`,
			}),
			providesTags: ["getAgencyNotes"],
		}),

		getManageAgencyMappedUsers: builder.query<GetManageAgencyMappedUsersListResponse, { agencyId: number }>({
			query: ({ agencyId }) => ({
				url: `manage/agency/${agencyId}/profile/mapped-users`,
			}),
			providesTags: ["getAgencyMappedUsers"],
		}),

		getManageAgencyUnmappedUsers: builder.query<GetManageAgencyUnmappedUsersListResponse, { agencyId: number }>({
			query: ({ agencyId }) => ({
				url: `manage/agency/${agencyId}/profile/unmapped-users`,
			}),
			providesTags: ["getAgencyUnmappedUsers"],
		}),

		getUserMappedAgenciesById: builder.query<GetUserMappedAgenciesListResponse, { userId: number }>({
			query: ({ userId }) => ({
				url: `manage/user/${userId}/agency-mapping`,
			}),
			providesTags: ["getUserMappedAgencies"],
		}),

		updateUserAgencyMapping: builder.mutation<
			GetManageAgencyUserSetupResponse,
			{ userId: number; data: { agency_ids: string } }
		>({
			query: ({ userId, data }) => ({
				url: `manage/user/${userId}/agency-mapping`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["getUserMappedAgencies"],
		}),

		chargeManageAgencyCreditCard: builder.mutation<
			ManageAgencyChargeCardResponse,
			{ agencyId: number; data: ManageAgencyBillingChargeCardPayload }
		>({
			query: ({ agencyId, data }) => ({
				url: `manage/agency/${agencyId}/billing/card/charge`,
				method: "POST",
				body: data,
			}),
		}),

		updateManageAgencyProfile: builder.mutation<
			DefaultApiSuccessResponse,
			{ agencyId: number; handle: AgencyProfileUpdateHandles; data: unknown }
		>({
			query: ({ agencyId, handle, data }) => {
				const payload = new URLSearchParams();
				Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
					payload.append(key, String(value));
				});
				return {
					url: `manage/agency/${agencyId}/profile/${handle}`,
					method: "POST",
					body: payload.toString(),
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
				};
			},
			invalidatesTags: (_result, _error, id) => {
				if (id.handle === "product") return ["getAgencyProducts"];
				if (id.handle === "user-mapping") return ["getAgencyMappedUsers", "getAgencyUnmappedUsers"];
				if (id.handle === "note") return ["getAgencyNotes"];
				return [];
			},
		}),

		sendPromotionToUsers: builder.mutation<DefaultApiSuccessResponse, { agencyId: number }>({
			query: ({ agencyId }) => ({
				url: `manage/agency/${agencyId}/profile/send-promotions`,
				method: "POST",
			}),
		}),

		getManageAgencyUsersList: builder.query<GetManageAgencyUsersListResponse, GetAgencyUsersListParams>({
			query: ({ agencyId, pageNo, pageSize, sorting, search = "", isAllUsers = false }) => ({
				url:
					isAllUsers === true ?
						`/manage/user/all/list/${sorting.id}/${sorting.dir}`
					:	`manage/agency/${agencyId}/user/list/${sorting.id}/${sorting.dir}`,
				params: {
					page_no: pageNo,
					limit: pageSize,
					search,
				},
			}),
			providesTags: ["getAgencyUsersList"],
		}),

		getManageAgencyUser: builder.query<GetManageAgencyUserResponse, { agencyId: number; userId: number }>({
			query: ({ agencyId, userId }) => ({
				url: `manage/agency/${agencyId}/user/${userId}`,
			}),
		}),

		getUserById: builder.query<GetManageAgencyUserResponse, { userId: number }>({
			query: ({ userId }) => ({
				url: `manage/user/${userId}`,
			}),
		}),

		deleteUserById: builder.mutation<DefaultApiSuccessResponse, { userId: number }>({
			query: ({ userId }) => ({
				url: `manage/user/${userId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["getAgencyUsersList"],
		}),

		toggleUserLockById: builder.mutation<GetManageAgencyUserSetupResponse, { userId: number; locked: boolean }>({
			query: ({ userId, locked }) => ({
				url: `manage/user/${userId}/toggle-lock`,
				method: "PUT",
				body: {
					status: locked === true ? "Y" : "N",
				},
			}),
			invalidatesTags: ["getAgencyUsersList"],
		}),

		setupManageAgencyUser: builder.mutation<
			GetManageAgencyUserSetupResponse,
			{ agencyId: number; userId: number; data: ManageAgencyUserSetupPayload }
		>({
			query: ({ agencyId, userId, data }) => ({
				url: userId > 0 ? `manage/agency/${agencyId}/user/${userId}` : `manage/agency/${agencyId}/user`,
				method: userId > 0 ? "PUT" : "POST",
				body: data,
			}),
			invalidatesTags: ["getAgencyUsersList"],
		}),

		deleteManageAgencyUser: builder.mutation<DefaultApiSuccessResponse, { agencyId: number; userId: number }>({
			query: ({ agencyId, userId }) => ({
				url: `manage/agency/${agencyId}/user/${userId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["getAgencyUsersList", "getManageUserList"],
		}),

		getManageAgencyUserEmailTemplates: builder.query<GetUserEmailTemplatesResponse, null>({
			query: () => ({
				url: `manage/agency/user/email-templates`,
			}),
		}),

		sendManageAgencyUserEmailTemplate: builder.mutation<
			DefaultApiSuccessResponse,
			{ data: { userId: number; templateId: number } }
		>({
			query: ({ data }) => ({
				url: "manage/agency/user/email-templates",
				method: "POST",
				body: {
					user_id: data.userId,
					template_id: data.templateId,
				},
			}),
		}),

		getManageUserList: builder.query<GetManageUserListResponse, GetAgencyAdminUserListParams>({
			query: ({ pageNo, pageSize, sorting, search = "" }) => ({
				url: `manage/user/list/${sorting.id}/${sorting.dir}`,
				params: {
					page_no: pageNo,
					limit: pageSize,
					search,
				},
			}),
			providesTags: ["getManageUserList"],
		}),

		setupManageUser: builder.mutation<
			GetManageAgencyUserSetupResponse,
			{ agencyId: number; userId: number; data: ManageUserSetupPayload }
		>({
			query: ({ userId = 0, data }) => ({
				url: userId > 0 ? `manage/user/${userId}` : "manage/user",
				method: userId > 0 ? "PUT" : "POST",
				body: data,
			}),
			invalidatesTags: ["getManageUserList"],
		}),
		getManageAgencyWebauthnConfig: builder.query<GetManageAgencyWebauthnConfigResponse, { agencyId: number }>({
			query: ({ agencyId }) => ({
				url: `manage/agency/${agencyId}/webauthn`,
			}),
			providesTags: ["getAgencyWhitelabel"],
		}),

		updateManageAgencyWebauthnConfig: builder.mutation<
			UpdateManageAgencyWebauthnConfigResponse,
			{ agencyId: number; data: UpdateManageAgencyWebauthnConfigPayload }
		>({
			query: ({ agencyId, data }) => ({
				url: `manage/agency/${agencyId}/webauthn`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["getAgencyWhitelabel"],
		}),
	}),
});

export const defaultAgencyMetaProfile = Object.assign({}, initialState);
export const {
	useGetManageAgencyProfileMetaQuery,
	useGetManagedAgencyListQuery,
	useGetManageAgencyWhitelabelQuery,
	useUpdateManageAgencyWhitelabelMutation,
	useGetManageAgencyBillingMetaQuery,
	useGetManageAgencyBillingCardsListQuery,
	useLazyGetManageAgencyBillingCardsListQuery,
	useGetManageAgencyBillingPaymentsQuery,
	useLazyGetManageAgencyBillingPaymentsQuery,
	useGetManageAgencyBillingUnpaidInvoicesQuery,
	useLazyGetManageAgencyBillingUnpaidInvoicesQuery,
	useGetManageAgencyProductsQuery,
	useLazyGetManageAgencyProductByIdQuery,
	useAddManageAgencyNewCardMutation,
	useChargeManageAgencyCreditCardMutation,
	useApplyManageAgencyInvoicesPaymentMutation,
	useWriteOffManageAgencyInvoiceMutation,
	useAdjustManageAgencyInvoiceAmountMutation,
	useUpdateManageAgencyProfileMutation,
	useSendPromotionToUsersMutation,
	useGetManageAgencyNotesQuery,
	useGetManageAgencyMappedUsersQuery,
	useGetManageAgencyUnmappedUsersQuery,
	useCreateNewManageAgencyMutation,
	useDeleteManageAgencyMutation,
	useGetManageAgencyUsersListQuery,
	useGetManageAgencyUserQuery,
	useLazyGetManageAgencyUserQuery,
	useGetManageAgencyUserEmailTemplatesQuery,
	useSendManageAgencyUserEmailTemplateMutation,
	useSetupManageAgencyUserMutation,
	useDeleteManageAgencyUserMutation,
	useGetManageUserListQuery,
	useSetupManageUserMutation,
	useGetUserByIdQuery,
	useLazyGetUserByIdQuery,
	useDeleteUserByIdMutation,
	useToggleUserLockByIdMutation,
	useGetUserMappedAgenciesByIdQuery,
	useUpdateUserAgencyMappingMutation,
	useGetManageAgencyWebauthnConfigQuery,
	useUpdateManageAgencyWebauthnConfigMutation,
	useDeleteManageAgencyProductByIdMutation,
} = accountManagementApiSlice;
